import React from 'react';
import { cssVar, VStack, Box, IconButton, Text } from '@cad-arc';
import { EventQueueView, ColumnDefinitionEnum } from '@mark43/cad-api';
import { WorkspaceQueueColumn } from '../../panels/workspace-queue-column';
import { EventLocationCaution } from '../../queues/components/cells/event-location-caution';
import { getEventQueueRowDataOrScheduledEventQueueRowData } from '../../queues/utils/get-event-queue-row-data';
import columnDefinitionEnum from '../../queues/column-definition.enum';
import { isValidLatLng } from '../../location/helpers';
import { EsriMapEntityTypeEnum } from '../../esri/types';
import { useViewLocationOnMap } from '../../location/hooks/use-view-location-on-map';
import { useCopyText } from '../../common/hooks/use-copy-text';
import { PriorityBox } from '../../common/components/fr-queues/priority-box';
import { BaseCellStyledBox } from '../../common/components/fr-queues/base-cell-styled-box';
import { FrTimeCell, FrMinutesCell } from '../../common/components/fr-queues/fr-time-cells';
import { FrTooltipCell } from '../../common/components/fr-queues/fr-tooltip-cell';
import { unitStatusColors } from '../../common/helpers/styles';
import { Tooltip } from '../../common/components/tooltip';
import { DataPrivacy } from '../../common/components/datadog-privacy';
import { FR_EVENT_QUEUE_UNMASKED_COLUMNS } from '../../common/components/fr-queues/constants';

export const FrCompactQueueCell = ({
    col,
    data,
}: {
    col: WorkspaceQueueColumn;
    data: EventQueueView;
}) => {
    const item = columnDefinitionEnum[col.columnDefinition];
    const viewLocationOnMap = useViewLocationOnMap();
    const copyText = useCopyText();
    const displayedUnits = data.assignedUnits.slice(0, 2);
    const truncateUnits = data.assignedUnits.slice(2);
    const dataToUse = getEventQueueRowDataOrScheduledEventQueueRowData(data);
    const isValidLocation = isValidLatLng(data.latitude, data.longitude);
    const handleViewOnMap = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (!isValidLocation || !data.id) return;
        viewLocationOnMap({
            entityId: data.id,
            entityType: EsriMapEntityTypeEnum.EVENT,
            latitude: data.latitude,
            longitude: data.longitude,
            shouldMoveMapCenterToLoggedInUnit: false,
        });
    };

    switch (item.name) {
        case ColumnDefinitionEnum.ASSIGNED_UNITS.name:
            return (
                <DataPrivacy privacy="unmask">
                    <BaseCellStyledBox>
                        {displayedUnits.map((unit) => {
                            const unitState = unitStatusColors[unit.unitStateGroup];
                            return (
                                <Box
                                    key={unit.id}
                                    backgroundColor={unitState.fill}
                                    borderRadius="4px"
                                    minHeight="22px"
                                    fontWeight={600}
                                    padding="1px 4px"
                                    marginRight="8px"
                                >
                                    <Text style={{ color: unitState.text }}>{unit.callSign}</Text>
                                </Box>
                            );
                        })}
                        {truncateUnits.length > 0 && (
                            <Tooltip
                                placement="right"
                                overlay={
                                    <VStack alignItems="center">
                                        {truncateUnits.map((unit) => {
                                            const unitState = unitStatusColors[unit.unitStateGroup];
                                            return (
                                                <Box
                                                    backgroundColor={unitState.fill}
                                                    margin="2px"
                                                    borderRadius="4px"
                                                    padding="2px 4px"
                                                    minWidth="50px"
                                                    key={unit.id}
                                                >
                                                    <Text style={{ color: unitState.text }}>
                                                        {unit?.callSign}
                                                    </Text>
                                                </Box>
                                            );
                                        })}
                                    </VStack>
                                }
                            >
                                <Text
                                    color="brand"
                                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                >
                                    +{truncateUnits.length}
                                </Text>
                            </Tooltip>
                        )}
                    </BaseCellStyledBox>
                </DataPrivacy>
            );
        case ColumnDefinitionEnum.EVENT_NUMBER.name:
            return (
                <DataPrivacy privacy="unmask">
                    <BaseCellStyledBox $justifyContent="space-between">
                        {item.getFlatValue(data)}
                        <IconButton
                            size="md"
                            icon="Duplicate"
                            variant="ghost"
                            style={{ marginLeft: 5 }}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                copyText({
                                    text: item.getFlatValue(data) ?? '',
                                    copiedFieldLabel: 'Event number',
                                });
                            }}
                        />
                    </BaseCellStyledBox>
                </DataPrivacy>
            );
        case ColumnDefinitionEnum.PRIORITY.name:
            return (
                <DataPrivacy privacy="unmask">
                    <BaseCellStyledBox
                        $fontSize={cssVar('arc.fontSizes.sm')}
                        $padding={cssVar('arc.space.half')}
                        borderRadius={cssVar('arc.radii.base')}
                    >
                        <PriorityBox isFlashing={data.isPriority1}>
                            {item.getFlatValue(data)}
                        </PriorityBox>
                    </BaseCellStyledBox>
                </DataPrivacy>
            );
        case ColumnDefinitionEnum.EVENT_LOCATION.name:
            if (dataToUse) {
                return (
                    <DataPrivacy privacy="mask">
                        <Box w="100%" as="button" type="button" onClick={handleViewOnMap}>
                            <BaseCellStyledBox $justifyContent="space-between" $padding={0}>
                                <FrTooltipCell text={item.getFlatValue(data)} />
                                <Box marginLeft="8px">
                                    <EventLocationCaution data={dataToUse} />
                                </Box>
                            </BaseCellStyledBox>
                        </Box>
                    </DataPrivacy>
                );
            }
            break;
        case ColumnDefinitionEnum.TIME_ARRIVED.name:
            return <DataPrivacy privacy="mask"><FrTimeCell dateStr={data.timeArrived} /></DataPrivacy>;
        case ColumnDefinitionEnum.TIME_RECEIVED.name:
            return <DataPrivacy privacy="mask"><FrTimeCell dateStr={data.timeReceived} /></DataPrivacy>;
        case ColumnDefinitionEnum.TIME_DISPATCHED.name:
            return <DataPrivacy privacy="mask"><FrTimeCell dateStr={data.timeDispatched} /></DataPrivacy>;
        case ColumnDefinitionEnum.MINUTES_STANDING.name:
            return <DataPrivacy privacy="mask"><FrMinutesCell dateStr={data.timeDispatched} /></DataPrivacy>;
        case ColumnDefinitionEnum.MINUTES_WORKING.name:
        case ColumnDefinitionEnum.TIME_TRANSMITTED.name:
            return <DataPrivacy privacy="mask"><FrMinutesCell dateStr={data.timeTransmitted} /></DataPrivacy>;
        default: {
            const privacy = FR_EVENT_QUEUE_UNMASKED_COLUMNS.has(item.name) ? 'unmask' : 'mask';
            return <DataPrivacy privacy={privacy}><FrTooltipCell text={item.getFlatValue(data)} /></DataPrivacy>;
        }
    }

    return <DataPrivacy privacy="mask"><FrTooltipCell text={item.getFlatValue(data)} /></DataPrivacy>;
};
